import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const LABEL = { appointment: "consultation", registration: "Academy seat" };

const usePaymentStatus = (sessionId) => {
  const [state, setState] = useState({ status: sessionId ? "checking" : "missing", kind: null });
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ status: "paid", kind: data.kind });
        if (data.payment_status === "expired") return setState({ status: "expired", kind: data.kind });
      } catch { /* retry below */ }
      tries += 1;
      if (tries >= 10) return setState({ status: "timeout", kind: null });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { clearTimeout(timer); };
  }, [sessionId]);
  return state;
};

const Card = ({ icon: Icon, title, text, children, testid }) => (
  <section className="min-h-screen flex items-center justify-center px-6 pt-28 pb-20">
    <div className="lux-card max-w-xl w-full p-10 md:p-14 text-center" data-testid={testid}>
      <Icon className="w-14 h-14 text-[#D4AF37] mx-auto" />
      <h1 className="mt-6 text-4xl font-medium">{title}</h1>
      <p className="mt-4 text-white/65">{text}</p>
      <div className="mt-10 flex flex-wrap gap-4 justify-center">{children}</div>
    </div>
  </section>
);

export default function PaymentResult() {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const { status, kind } = usePaymentStatus(pathname.endsWith("success") ? params.get("session_id") : null);
  const what = LABEL[kind] || "booking";

  if (pathname.endsWith("cancel")) return (
    <Card icon={XCircle} testid="payment-cancelled" title="Payment cancelled" text="No charge was made. Your spot is not reserved until payment is completed.">
      <Link to={params.get("kind") === "registration" ? "/academy" : "/book"} className="btn-gold" data-testid="payment-try-again-link">Try Again</Link>
      <Link to="/contact" className="btn-glass">Contact Us</Link>
    </Card>
  );
  if (status === "checking") return <Card icon={Loader2} testid="payment-checking" title="Confirming your payment…" text="This only takes a moment. Please don't close this page." />;
  if (status === "paid") return (
    <Card icon={CheckCircle2} testid="payment-success" title="Payment received" text={`Thank you! Your ${what} is confirmed and a confirmation email is on its way.`}>
      <Link to="/portal" className="btn-gold" data-testid="payment-portal-link">View in Client Portal</Link>
      <Link to="/" className="btn-glass">Back Home</Link>
    </Card>
  );
  return (
    <Card icon={XCircle} testid="payment-failed" title={status === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}
      text="If you were charged, please contact us and we'll sort it out right away. Otherwise, please try booking again.">
      <Link to="/book" className="btn-gold">Book Again</Link>
      <Link to="/contact" className="btn-glass">Contact Us</Link>
    </Card>
  );
}
